import React, { useState, Fragment, useRef } from "react";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import {
	isValidPassword,
	passwordsMatch,
	isValidEmail,
	normalizeEmail,
} from "../../../js/formUtils";

/**
 * ForgotPassword Component - Implements AWS Amplify forgot password flow
 *
 * Props:
 * @param {function} onCancel - Callback function to return to login
 * @param {function} onPasswordResetSuccess - Callback function called after successful password reset
 * @param {string} email - Initial email value
 */

function ForgotPassword(props) {
	const codeLength = 6;
	const [email, setEmail] = useState(props.email || "");
	const [codeDigits, setCodeDigits] = useState(Array(codeLength).fill(""));
	const [verificationCode, setVerificationCode] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmNewPassword, setConfirmNewPassword] = useState("");
	const [newPasswordTouched, setNewPasswordTouched] = useState(false);
	const [confirmNewPasswordTouched, setConfirmNewPasswordTouched] =
		useState(false);
	const [error, setError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [step, setStep] = useState(1); // 1: email, 2: code + password
	const inputRefs = useRef([]);
	const firstInputRef = useRef(null);

	/**
	 * Step 1: Send forgot password verification code
	 */
	const sendForgotPasswordCode = async () => {
		if (!isEmailValid()) {
			setError("Please enter a valid email address.");
			return;
		}

		setIsSubmitting(true);
		setError("");

		try {
			await resetPassword({ username: email });
			setStep(2); // Move to verification step
		} catch (error) {
			setError(
				error.message || "Failed to send verification code. Please try again.",
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	/**
	 * Step 2: Submit verification code and new password
	 */
	const submitNewPassword = async () => {
		setIsSubmitting(true);
		setError("");

		console.log("submitNewPassword", verificationCode);
		if (!verificationCode || verificationCode.length !== 6) {
			setError("Please enter a valid 6-digit verification code.");
			setIsSubmitting(false);
			return;
		}

		if (!isNewPasswordFormValid()) {
			setError("Please enter a valid password and make sure passwords match.");
			setIsSubmitting(false);
			return;
		}

		try {
			await confirmResetPassword({
				username: email,
				confirmationCode: verificationCode,
				newPassword,
			});

			// Call success callback to auto-login
			if (props.onPasswordResetSuccess) {
				props.onPasswordResetSuccess(email, newPassword);
			} else {
				setError("Failed to reset password. Try using a different password.");
				setIsSubmitting(false);
			}
		} catch (error) {
			console.error("Error resetting password:", error);
			setError(error.message || "Failed to reset password. Please try again.");
		}
	};

	// Handle email normalization
	const handleEmailInput = (value) => {
		const normalized = normalizeEmail(value);
		setEmail(normalized);
		return normalized;
	};

	// Handle code input (from VerifyAccount component)
	const handleCodeInput = (e, index) => {
		const value = e.currentTarget.value;
		if (!/^[0-9]*$/.test(value)) return; // Only allow numbers
		const singleDigit = value.slice(-1);

		const newCodeDigits = [...codeDigits];
		newCodeDigits[index] = singleDigit;
		setCodeDigits(newCodeDigits);

		// Build complete code
		const completeCode = newCodeDigits.join("");
		setVerificationCode(completeCode);

		// Auto-focus next input
		if (singleDigit && index < codeLength - 1) {
			const nextInput = inputRefs.current[index + 1];
			if (nextInput) nextInput.focus();
		}
	};

	const handleKeyDown = (e, index) => {
		// Handle backspace
		if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
			const prevInput = inputRefs.current[index - 1];
			if (prevInput) {
				prevInput.focus();
				const newCodeDigits = [...codeDigits];
				newCodeDigits[index - 1] = "";
				setCodeDigits(newCodeDigits);
				setVerificationCode(newCodeDigits.join(""));
			}
		}
	};

	const handlePaste = (e) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData("text");
		const digits = pastedData.replace(/\D/g, "").slice(0, codeLength);

		if (digits.length > 0) {
			const newCodeDigits = Array(codeLength).fill("");
			for (let i = 0; i < digits.length; i++) {
				newCodeDigits[i] = digits[i];
			}
			setCodeDigits(newCodeDigits);
			setVerificationCode(newCodeDigits.join(""));

			// Focus the next empty input or the last input
			const nextIndex = Math.min(digits.length, codeLength - 1);
			const nextInput = inputRefs.current[nextIndex];
			if (nextInput) nextInput.focus();
		}
	};

	// Form validation
	const isEmailValid = () => isValidEmail(email);
	const isNewPasswordFormValid = () => {
		return (
			isValidPassword(newPassword) &&
			passwordsMatch(newPassword, confirmNewPassword)
		);
	};

	// Step 1: Handle email submission
	const handleEmailSubmit = (e) => {
		e.preventDefault();
		sendForgotPasswordCode();
	};

	// Step 2: Handle verification code and password submission
	const handlePasswordResetSubmit = (e) => {
		e.preventDefault();
		submitNewPassword();
	};

	return (
		<Fragment>
			{step === 1 ? (
				// Step 1: Email input
				<Fragment>
					<h3>Reset Password</h3>
					<p>
						Enter your email address and we'll send you a verification code to
						reset your password.
					</p>

					{error && <div className="error-message">{error}</div>}

					<form onSubmit={handleEmailSubmit} className="login-form">
						<div className="form-group">
							<input
								type="email"
								id="reset-email"
								value={email}
								placeholder=" "
								onChange={(e) => handleEmailInput(e.currentTarget.value)}
								required
								disabled={isSubmitting}
								autoComplete="email"
							/>
							<label htmlFor="reset-email">Email Address</label>
							{email && !isEmailValid() && (
								<div className="field-error">
									Please enter a valid email address
								</div>
							)}
						</div>

						<div className="form-actions">
							<button
								type="submit"
								className="submit-button"
								disabled={!isEmailValid() || isSubmitting}>
								{isSubmitting ? "Sending Code..." : "Send Verification Code"}
							</button>
						</div>
					</form>

					<div className="form-links">
						<a
							href="#"
							className="form-text-link"
							onClick={(e) => {
								e.preventDefault();
								props.onCancel();
							}}>
							Back to Login
						</a>
					</div>
				</Fragment>
			) : (
				// Step 2: Verification code and new password
				<Fragment>
					<h3>Set New Password</h3>
					<p>
						You should recieve a verification code at <strong>{email}</strong>{" "}
						within a few minutes. Enter the code and your new password below.
					</p>

					{error && <div className="error-message">{error}</div>}

					<form onSubmit={handlePasswordResetSubmit} className="login-form">
						<div className="confirmation-form">
							<div className="confirmation-input-group">
								<label>Verification Code</label>
								<div className="verification-code-container">
									{codeDigits.map((digit, index) => (
										<input
											key={index}
											type="text"
											inputMode="numeric"
											pattern="[0-9]*"
											maxLength="1"
											value={digit}
											onChange={(e) => handleCodeInput(e, index)}
											onKeyDown={(e) => handleKeyDown(e, index)}
											onFocus={(e) => e.currentTarget.select()}
											onPaste={(e) => handlePaste(e)}
											ref={(el) => {
												if (el) {
													inputRefs.current[index] = el;
													if (index === 0) firstInputRef.current = el;
												}
											}}
											disabled={isSubmitting}
											className="code-input"
											aria-label={`Digit ${index + 1}`}
											autoComplete="off"
										/>
									))}
								</div>
							</div>
						</div>

						<div className="form-group">
							<input
								type="password"
								id="new-password"
								value={newPassword}
								placeholder=" "
								onChange={(e) => setNewPassword(e.currentTarget.value)}
								onBlur={() => setNewPasswordTouched(true)}
								required
								disabled={isSubmitting}
								autoComplete="new-password"
							/>
							<label htmlFor="new-password">New Password</label>
							{newPasswordTouched &&
								newPassword &&
								!isValidPassword(newPassword) && (
									<div className="field-error">
										Password must be at least 8 characters
									</div>
								)}
						</div>

						<div className="form-group">
							<input
								type="password"
								id="confirm-new-password"
								value={confirmNewPassword}
								placeholder=" "
								onChange={(e) => setConfirmNewPassword(e.currentTarget.value)}
								onBlur={() => setConfirmNewPasswordTouched(true)}
								required
								disabled={isSubmitting}
								autoComplete="new-password"
							/>
							<label htmlFor="confirm-new-password">Confirm New Password</label>
							{confirmNewPasswordTouched &&
								confirmNewPassword &&
								newPassword !== confirmNewPassword && (
									<div className="field-error">Passwords do not match</div>
								)}
						</div>

						<div className="form-actions">
							<button
								type="submit"
								className="submit-button"
								disabled={
									!isNewPasswordFormValid() || !verificationCode || isSubmitting
								}>
								{isSubmitting ? "Resetting Password..." : "Reset Password"}
							</button>
						</div>
					</form>

					<div className="form-links">
						<a
							href="#"
							className="form-text-link"
							onClick={(e) => {
								e.preventDefault();
								setStep(1);
								setError("");
								setVerificationCode("");
								setCodeDigits(Array(codeLength).fill(""));
								setNewPassword("");
								setConfirmNewPassword("");
								setNewPasswordTouched(false);
								setConfirmNewPasswordTouched(false);
							}}>
							Previous Step
						</a>
						{" | "}
						<a
							href="#"
							className="form-text-link"
							onClick={(e) => {
								e.preventDefault();
								props.onCancel();
							}}>
							Return to Login
						</a>
					</div>
				</Fragment>
			)}
		</Fragment>
	);
}

export default ForgotPassword;
